import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { RecItem, GOAL_LABELS } from './RecommendationsCard';
import { FdaDisclaimer, FtcDisclosure } from './Disclosures';

// SS4 — the funnel quiz. A short, goal-first form that runs the SAME honest,
// evidence-graded, safety-gated engine for someone with zero tracker data (even
// anonymous — it POSTs the public /api/recommendations/quiz endpoint). It is
// willing to answer "food first" or "save your money", shows every evidence
// grade, and surfaces medication / pregnancy flags as calm flag-and-defer notes.
// No account required; a soft CTA invites signing up to track whether it works.

const GOAL_HINTS = {
  sleep: 'Fall asleep, stay asleep, wake rested',
  energy: 'Daytime energy and fighting fatigue',
  immunity: 'Everyday immune support',
  stress: 'Calm, resilience, a steadier mood',
  muscle: 'Build and keep muscle',
  heart_brain: 'Heart and cognitive health',
  digestion: 'Gut comfort and regularity',
  bone: 'Bone strength as you age',
  general_longevity: 'Overall healthspan',
};

const COMMON_ALLERGENS = ['fish', 'shellfish', 'soy', 'dairy', 'gluten', 'tree nuts'];
const PREGNANCY_OPTIONS = [
  { value: '', label: 'Not applicable' },
  { value: 'trying', label: 'Trying to conceive' },
  { value: 'pregnant', label: 'Pregnant' },
  { value: 'breastfeeding', label: 'Breastfeeding' },
];

const chipCls = (on) =>
  `text-sm px-3 py-1.5 rounded-full border transition-colors ${
    on
      ? 'bg-primary-600 border-primary-600 text-white'
      : 'border-surface-border dark:border-surface-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-raised/50'
  }`;

const SupplementQuiz = ({
  heading = 'What are you hoping to improve?',
  subheading = 'Pick a goal or two. We’ll give you honest, evidence-graded suggestions — including when food does the job or you can skip the pill.',
  showSignupCta = true,
}) => {
  const [step, setStep] = useState('goals'); // goals | safety | results
  const [allGoals, setAllGoals] = useState([]);
  const [goals, setGoals] = useState([]);
  const [pregnancy, setPregnancy] = useState('');
  const [allergens, setAllergens] = useState([]);
  const [medsText, setMedsText] = useState('');
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get('/api/recommendations/goals')
      .then((res) => setAllGoals(res.data?.goals || []))
      .catch(() => {});
  }, []);

  const toggle = (setter) => (v) =>
    setter((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const submit = async () => {
    setLoading(true);
    setStep('results');
    try {
      const medications = medsText
        .split(',').map((m) => m.trim()).filter(Boolean);
      const res = await apiClient.post('/api/recommendations/quiz', {
        goals,
        allergies: allergens,
        pregnancy_status: pregnancy || null,
        medications,
      });
      setRecs(res.data?.recommendations || []);
    } catch {
      setRecs([]);
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setRecs(null);
    setStep('goals');
  };

  const hasAffiliate = (recs || []).some((r) => r.affiliate_url);

  // --- Step 1: goals --------------------------------------------------------
  if (step === 'goals') {
    return (
      <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-6 space-y-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{heading}</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{subheading}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {allGoals.map((g) => {
            const on = goals.includes(g);
            return (
              <button
                key={g}
                type="button"
                onClick={() => toggle(setGoals)(g)}
                className={`text-left rounded-xl border p-3 transition-colors ${
                  on
                    ? 'border-primary-600 bg-primary-500/10'
                    : 'border-surface-border dark:border-surface-dark-border hover:bg-gray-50 dark:hover:bg-surface-dark-raised/50'
                }`}
              >
                <span className="block text-base font-semibold text-gray-900 dark:text-white">
                  {GOAL_LABELS[g] || g}
                </span>
                <span className="block text-sm text-gray-500 dark:text-gray-400">
                  {GOAL_HINTS[g] || ''}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            disabled={goals.length === 0}
            onClick={() => setStep('safety')}
            className="text-sm font-semibold px-4 py-2 rounded-full bg-primary-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary-700 transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  // --- Step 2: optional safety ---------------------------------------------
  if (step === 'safety') {
    return (
      <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-6 space-y-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Anything we should factor in?
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            All optional — it just lets us keep suggestions safe for you. We never
            share this, and we never suggest changing a medication.
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Any food allergies?
          </label>
          <div className="flex flex-wrap gap-2">
            {COMMON_ALLERGENS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => toggle(setAllergens)(a)}
                className={chipCls(allergens.includes(a))}
              >
                {a}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Pregnancy status
          </label>
          <select
            value={pregnancy}
            onChange={(e) => setPregnancy(e.target.value)}
            className="w-full text-sm rounded-lg border border-surface-border dark:border-surface-dark-border bg-white dark:bg-surface-dark-raised px-3 py-2 text-gray-900 dark:text-white"
          >
            {PREGNANCY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
            Any medications? <span className="text-gray-400">(comma-separated)</span>
          </label>
          <input
            type="text"
            value={medsText}
            onChange={(e) => setMedsText(e.target.value)}
            placeholder="e.g. warfarin, levothyroxine"
            className="w-full text-sm rounded-lg border border-surface-border dark:border-surface-dark-border bg-white dark:bg-surface-dark-raised px-3 py-2 text-gray-900 dark:text-white placeholder-gray-400"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            We’ll flag possible interactions and tell you to check with your
            pharmacist — that’s as far as we go.
          </p>
        </div>

        <div className="flex justify-between">
          <button
            type="button"
            onClick={() => setStep('goals')}
            className="text-sm font-medium px-4 py-2 rounded-full border border-surface-border dark:border-surface-dark-border text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-raised/50 transition-colors"
          >
            Back
          </button>
          <button
            type="button"
            onClick={submit}
            className="text-sm font-semibold px-4 py-2 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            See my suggestions
          </button>
        </div>
      </div>
    );
  }

  // --- Step 3: results ------------------------------------------------------
  return (
    <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Your suggestions
        </h2>
        <button
          type="button"
          onClick={restart}
          className="text-sm font-medium text-primary-700 dark:text-primary-300 hover:underline"
        >
          Start over
        </button>
      </div>
      {goals.length > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          For: {goals.map((g) => GOAL_LABELS[g] || g).join(', ')}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Thinking…</p>
      ) : (recs || []).length === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Nothing worth suggesting here — and that’s often the honest answer. Food
          and habits carry most of the weight for these goals.
        </p>
      ) : (
        <div className="space-y-3">
          {recs.map((r, i) => <RecItem key={`${r.goal}-${r.slug}-${i}`} r={r} />)}
        </div>
      )}

      {showSignupCta && !loading && (
        <div className="rounded-xl border border-primary-500/40 bg-primary-500/5 p-4">
          <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">
            Want to know if it’s actually working?
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Create a free account to log your stack and watch the numbers that
            matter to you move — honestly graded, never hyped.
          </p>
          <Link
            to="/register"
            className="mt-3 inline-flex items-center text-sm font-semibold px-4 py-2 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
          >
            Create a free account
          </Link>
        </div>
      )}

      <div className="pt-2 border-t border-surface-border dark:border-surface-dark-border space-y-2">
        <FtcDisclosure hasAffiliate={hasAffiliate} />
        <FdaDisclaimer />
      </div>
    </div>
  );
};

export default SupplementQuiz;
